export class CycleException extends Error {
    constructor(task) {
        super(`Cycle detected in task dependencies: ${task.name || task._id}`);
        this.name = "CycleException";
    }
}
export class DepthFirstGraphSort {
    m_projectTasks;
    m_includeTask;
    m_tasks = [];
    m_temporaryMark = new Set();
    m_permanentMark = new Set();
    m_taskById = new Map();
    /** Successor links implied by each task's predecessors array (the other side of the graph). */
    m_successorsFromPredecessors = new Map();
    constructor(tasks, filter) {
        this.m_projectTasks = tasks;
        this.m_taskById = new Map(this.m_projectTasks.map(t => [t.unique_id, t]));
        this.m_includeTask = filter;
        this.indexSuccessorLinksFromPredecessors();
    }
    indexSuccessorLinksFromPredecessors() {
        for (const task of this.m_projectTasks) {
            for (const rel of task.predecessors || []) {
                const predId = rel.predecessor_task_unique_id ?? rel.task_unique_id;
                if (predId == null)
                    continue;
                const succId = rel.successor_task_unique_id ?? task.unique_id;
                const list = this.m_successorsFromPredecessors.get(predId) ?? [];
                if (!list.some(existing => (existing.successor_task_unique_id ?? 0) === succId)) {
                    list.push({
                        ...rel,
                        predecessor_task_unique_id: predId,
                        successor_task_unique_id: succId,
                    });
                    this.m_successorsFromPredecessors.set(predId, list);
                }
            }
        }
    }
    sort() {
        try {
            for (const task of this.m_projectTasks) {
                if (this.m_includeTask(task)) {
                    this.visit(task);
                }
            }
            return [...this.m_tasks].reverse();
        }
        finally {
            this.m_tasks = [];
            this.m_temporaryMark.clear();
            this.m_permanentMark.clear();
        }
    }
    /**
     * Retrieve the successors for the given task.
     * Note this method is intended to be overridden to augment the original functionality.
     *
     * @param task task from which to generate list of successors
     * @return list of successor relationships
     */
    getSuccessors(task) {
        const fromField = task.successors || [];
        const fromPredecessors = this.m_successorsFromPredecessors.get(task.unique_id) || [];
        if (fromPredecessors.length === 0)
            return fromField;
        const seen = new Set(fromField.map(rel => rel.successor_task_unique_id).filter((id) => id != null));
        const merged = [...fromField];
        for (const rel of fromPredecessors) {
            const succId = rel.successor_task_unique_id;
            if (succId == null || seen.has(succId))
                continue;
            seen.add(succId);
            merged.push(rel);
        }
        return merged;
    }
    /**
     * Get the actual TaskDoc for a successor relationship.
     *
     * @param relation the relationship containing the successor task unique ID
     * @return the TaskDoc for the successor, or null if not found
     */
    getSuccessorTask(relation) {
        const id = relation.successor_task_unique_id;
        if (id === undefined) {
            return null;
        }
        return this.m_taskById.get(id) || null;
    }
    /**
     * Recursively build the sorted list of tasks.
     *
     * @param task current task
     */
    visit(task) {
        if (this.m_permanentMark.has(task)) {
            return;
        }
        if (this.m_temporaryMark.has(task)) {
            // TODO: Can we break a cycle by ignoring relation where cycle detected?
            throw new CycleException(task);
        }
        this.m_temporaryMark.add(task);
        for (const relation of this.getSuccessors(task)) {
            const successorTask = this.getSuccessorTask(relation);
            if (successorTask && this.m_includeTask(successorTask)) {
                this.visit(successorTask);
            }
        }
        this.m_temporaryMark.delete(task);
        this.m_permanentMark.add(task);
        this.m_tasks.push(task);
    }
}
